import React, { useMemo } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from 'components/common/PageHeader';
import SoftBadge from 'components/common/SoftBadge';
import { useAuth } from 'context/AuthContext';
import { PENDING_MODULE_TOUR_KEY } from 'components/tour/AppJoyride';
import {
  findHelpModuleByPath,
  getHelpModulesWithAccess
} from 'components/help/helpRegistry';

const HelpCenterPage = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const modules = useMemo(() => getHelpModulesWithAccess(auth), [auth]);
  const moduleKey = searchParams.get('module');
  const selectedModule = modules.find(module => module.key === moduleKey);
  const currentModule = selectedModule || findHelpModuleByPath(pathname);
  const currentModuleWithAccess = currentModule
    ? modules.find(module => module.key === currentModule.key)
    : null;
  const featuredModule = currentModuleWithAccess || modules[0];
  const startSelectedModuleGuide = module => {
    if (!module?.route) return;
    window.sessionStorage.setItem(
      PENDING_MODULE_TOUR_KEY,
      JSON.stringify({ moduleKey: module.key })
    );
    navigate(module.route);
  };

  return (
    <>
      <PageHeader
        title={t('HELP_CENTER.TITLE')}
        description={t('HELP_CENTER.SUBTITLE')}
        className="mb-3"
      >
        <Button
          size="sm"
          variant="falcon-primary"
          onClick={() => startSelectedModuleGuide(featuredModule)}
          disabled={!featuredModule?.hasAccess}
        >
          <FontAwesomeIcon icon="play" className="me-2" />
          {t('HELP_CENTER.START_CURRENT_TOUR')}
        </Button>
      </PageHeader>

      {featuredModule ? (
        <Card className="mb-3" data-tour="help-featured-module">
          <Card.Body>
            <Row className="g-3 align-items-start">
              <Col lg={8}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={featuredModule.icon} className="text-primary" />
                  <h5 className="mb-0">{t(featuredModule.titleKey)}</h5>
                  <SoftBadge bg="primary" className="ms-1">
                    {t('HELP_CENTER.RECOMMENDED')}
                  </SoftBadge>
                </div>
                <p className="text-700 mb-3">{t(featuredModule.descriptionKey)}</p>
                <ul className="mb-0 ps-3">
                  {featuredModule.sections.map(sectionKey => (
                    <li key={sectionKey} className="mb-2 text-700">
                      {t(sectionKey)}
                    </li>
                  ))}
                </ul>
              </Col>
              <Col lg={4} className="d-flex flex-column gap-2">
                {!featuredModule.hasAccess ? (
                  <div className="alert alert-warning py-2 mb-3">
                    {t('HELP_CENTER.PERMISSION_REQUIRED')}
                  </div>
                ) : null}
                {featuredModule.hasAccess ? (
                  <Button as={Link} to={featuredModule.route} variant="primary">
                    <FontAwesomeIcon icon="external-link-alt" className="me-2" />
                    {t('HELP_CENTER.OPEN_MODULE')}
                  </Button>
                ) : (
                  <Button variant="primary" disabled>
                    <FontAwesomeIcon icon="external-link-alt" className="me-2" />
                    {t('HELP_CENTER.OPEN_MODULE')}
                  </Button>
                )}
                <Button
                  variant="falcon-primary"
                  onClick={() => startSelectedModuleGuide(featuredModule)}
                  disabled={!featuredModule.hasAccess}
                >
                  <FontAwesomeIcon icon="route" className="me-2" />
                  {t('HELP_CENTER.START_MODULE_TOUR')}
                </Button>
                <div className="fs--1 text-600">
                  {t('HELP_CENTER.MEDIA_PLACEHOLDER')}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : null}

      <Row className="g-3" data-tour="help-module-list">
        {modules.map(module => (
          <Col md={6} xl={4} key={module.key}>
            <Card className="h-100">
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={module.icon} className="text-primary" />
                  <h6 className="mb-0">{t(module.titleKey)}</h6>
                </div>
                <p className="text-600 fs--1 flex-grow-1">{t(module.descriptionKey)}</p>
                <div className="d-flex flex-wrap gap-2">
                  {module.hasAccess ? (
                    <Button as={Link} to={module.route} size="sm" variant="falcon-primary">
                      {t('HELP_CENTER.OPEN_MODULE')}
                    </Button>
                  ) : (
                    <Button size="sm" variant="falcon-primary" disabled>
                      {t('HELP_CENTER.OPEN_MODULE')}
                    </Button>
                  )}
                  <Button
                    as={Link}
                    to={`/help?module=${module.key}`}
                    size="sm"
                    variant="falcon-default"
                  >
                    {t('HELP_CENTER.READ_MANUAL')}
                  </Button>
                  {!module.hasAccess ? (
                    <span className="fs--2 text-600 align-self-center">
                      {t('HELP_CENTER.PERMISSION_BADGE')}
                    </span>
                  ) : null}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
};

export default HelpCenterPage;
